var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var _a;
import { useCallback } from 'react';
import { ComposerAnalyticsEvent, } from '../types/component';
/**
 * Canonical Amplitude event names for the shared EventComposer's abstract
 * analytics events. Kept in one place so every hosting app reports the same
 * names.
 */
export var composerAmplitudeEventNames = (_a = {},
    _a[ComposerAnalyticsEvent.COMPOSER_OPENED] = 'opened-event-composer',
    _a[ComposerAnalyticsEvent.EVENT_CREATED] = 'created-event',
    _a[ComposerAnalyticsEvent.EVENT_UPDATED] = 'updated-event',
    _a[ComposerAnalyticsEvent.EVENT_SYNC_FAILED] = 'failed-event-sync',
    _a[ComposerAnalyticsEvent.COMPOSER_ABANDONED] = 'abandoned-event-composer',
    _a[ComposerAnalyticsEvent.OPENED_ADD_SPACE_SIDER] = 'opened-add-space-sider',
    _a[ComposerAnalyticsEvent.ADDED_SPACE] = 'added-space-to-event',
    _a[ComposerAnalyticsEvent.ADDED_SUGGESTED_SPACE] = 'added-suggested-space-to-event',
    _a[ComposerAnalyticsEvent.OPENED_SUGGESTED_TIMES_SIDER] = 'opened-suggested-times-sider',
    _a[ComposerAnalyticsEvent.SELECTED_SUGGESTED_TIME] = 'selected-suggested-time',
    _a[ComposerAnalyticsEvent.SELECTED_RECURRENCE] = 'selected-recurrence',
    _a[ComposerAnalyticsEvent.ADDED_EXTERNAL_INVITEE] = 'added-external-invitee',
    _a[ComposerAnalyticsEvent.EVENT_CANCELED] = 'canceled-event',
    // Intentionally shares its name with schedule's legacy week-schedule event;
    // the `source: 'event-composer'` property distinguishes the surfaces while
    // keeping one metric for "space removed from event".
    _a[ComposerAnalyticsEvent.REMOVED_SPACE] = 'removed-space-from-event',
    _a[ComposerAnalyticsEvent.SWITCHED_TO_LEGACY_COMPOSER] = 'switched-to-legacy-composer',
    _a);
/**
 * Stamped onto every composer event's metadata so composer-originated events
 * are unambiguously filterable in Amplitude — even when an event name is
 * shared with another surface (e.g. `removed-space-from-event`) or when two
 * hosts report the same `entryPoint` (shell-over-office vs office's own
 * renderer).
 */
export var COMPOSER_EVENT_SOURCE = 'event-composer';
/**
 * Dashboard routes are prefixed with the org slug (`/:orgSlug/...`). Splits
 * the slug out of the pathname so paths and urls are comparable across orgs
 * in Amplitude, and reports the slug as its own metadata field.
 */
var getPageMetadata = function () {
    if (typeof window === 'undefined') {
        return { page_url: undefined, page_path: undefined, org_slug: undefined };
    }
    var _a = window.location, origin = _a.origin, pathname = _a.pathname, search = _a.search, hash = _a.hash;
    var _b = __read(pathname.split('/')), orgSlug = _b[1], rest = _b.slice(2);
    // Most (all) places we load the event composer should have the orgslug in the path.
    // So even though this is a little brittle, it should be okay. Alternative is passing
    // the org slug in as a prop, but I didn't want to add overhead just for amplitude.
    var pagePath = "/".concat(rest.join('/'));
    return {
        page_url: "".concat(origin).concat(pagePath).concat(search).concat(hash),
        page_path: pagePath,
        org_slug: orgSlug || undefined,
    };
};
/**
 * Builds the `onTrackComposerEvent` callback for `EventComposer`: maps the
 * composer's abstract `ComposerAnalyticsEvent` types to the canonical
 * Amplitude names and stamps `source` + the host's `entryPoint` onto the
 * metadata.
 */
export var useTrackComposerEvent = function (_a) {
    var trackEvent = _a.trackEvent, entryPoint = _a.entryPoint;
    return useCallback(function (eventType, metadata) {
        trackEvent(composerAmplitudeEventNames[eventType], __assign(__assign({ source: COMPOSER_EVENT_SOURCE, entryPoint: entryPoint }, getPageMetadata()), metadata));
    }, [trackEvent, entryPoint]);
};
